import React, { useState, useEffect, useCallback } from 'react'
import type { QuizScript, QuizScriptApiInterface, SolveResult } from '../../types/quizScript'
import type { QuizSolveResult } from '../../types/legacyQuiz'
import { QuizRenderer } from '../quiz-types/quiz-renderer'
import { QuizHintToggle } from './QuizHintToggle'
import { FloatingQueBoard } from '../tools/QueBoard/FloatingQueBoard'
import { getQueBoardPresetsForQuiz } from '../tools/QueBoard/quizPresets'
import { QuizResultPopup } from '../quiz-types/shared/quiz-result-popup'
import { isConsecutiveThree, type StreakEntry } from '../quiz-types/shared/hooks/use-quiz-streak'
import type { QuizMode } from '../quiz-types/quiz-mode-context'
import {
  QUIZ_SESSION_CARD_END_STYLES,
  QUIZ_SESSION_NEXT_STYLES,
} from '../quiz-types/shared/quiz-styles'
import { useQuizStyleVariant } from '../quiz-types/shared/quiz-style-context'
import { cn } from '../../lib/utils'

export interface QuizResponseResult {
  isCorrect: boolean
  corrects?: boolean[]
  correctAnswers?: any[]
  description?: { text?: string; imageUrl?: string; videoUrl?: string }
}

export interface QuizResponseData {
  quizId: string
  sequenceNumber: number
  scriptId?: string
  answers: any[]
  timeSpentSec?: number
  elapsedMs?: number
  // LC 진단 AIQ: 백엔드 재채점 일관성을 위해 출제에 쓰인 변수 스냅샷을 함께 전송
  vars?: Record<string, unknown>
}

interface QuizContainerProps {
  quiz: QuizScript
  contentId?: number
  sequenceNumber: number
  onComplete?: (result: { quizId: string; isCorrect: boolean; score: number }) => void
  showResultImmediately?: boolean
  quizScriptApi: QuizScriptApiInterface
  // Hint 표시 여부 (Configuration에서 제어)
  showHint?: boolean
  // Learning Session API (optional)
  sessionId?: number
  onQuizResponse?: (data: QuizResponseData) => Promise<QuizResponseResult>
  streak?: StreakEntry[]
  streakIndex?: number
  resultDisplayMode?: 'inline' | 'popup'
  disableSound?: boolean
  assessmentMode?: boolean
  assessmentActionPlacement?: 'fixed-bottom' | 'card-end'
  onAdvance?: () => void
  nextLabel?: string
  mode?: QuizMode
}

/**
 * Convert SolveResult → QuizSolveResult
 * for quiz-types/ components that expect the legacy format.
 */
function toQuizSolveResult(solveResult: SolveResult): QuizSolveResult {
  return {
    correct: solveResult.correct,
    corrects: solveResult.corrects,
    correctAnswers: (solveResult.correctAnswers ?? []).map(String),
    description: solveResult.description
      ? { text: solveResult.description.text ?? undefined }
      : undefined,
  }
}

export const QuizContainer: React.FC<QuizContainerProps> = ({
  quiz,
  contentId: _contentId,
  sequenceNumber,
  onComplete,
  showHint = true,
  quizScriptApi,
  sessionId,
  onQuizResponse,
  streak = [],
  streakIndex = 0,
  resultDisplayMode = 'inline',
  disableSound = false,
  assessmentMode = false,
  assessmentActionPlacement = 'fixed-bottom',
  onAdvance,
  nextLabel,
  mode,
}) => {
  const [solveResult, setSolveResult] = useState<QuizSolveResult | null>(null)
  const [queBoardOpen, setQueBoardOpen] = useState(false)
  const [popup, setPopup] = useState<null | { isCorrect: boolean; isCombo: boolean }>(null)
  const [currentAnswers, setCurrentAnswers] = useState<string[]>([])
  const [mountTime] = useState(() => Date.now())
  const [submitting, setSubmitting] = useState(false)
  const variant = useQuizStyleVariant()
  const isAiq = variant === 'aiq'
  const isCardEndAssessment = assessmentMode && assessmentActionPlacement === 'card-end'
  const isDone = currentAnswers.length > 0 && currentAnswers.every((a) => a !== '' && a != null)

  const quizKey = quiz.quizId || quiz.scriptId || ''
  const queBoardPresets = getQueBoardPresetsForQuiz(quizKey)

  // Reset when quiz changes
  useEffect(() => {
    setSolveResult(null)
    setQueBoardOpen(false)
    setPopup(null)
  }, [quizKey])

  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      _vars: Record<string, unknown>,
      elapsedTime: number
    ) => {
      const timeSpentSeconds = Math.floor(elapsedTime / 1000)
      const quizId = quiz.quizId || quiz.scriptId || ''

      try {
        if (sessionId && onQuizResponse) {
          // Learning Session API path
          const apiResult = await onQuizResponse({
            quizId,
            sequenceNumber,
            scriptId: quiz.scriptId ?? undefined,
            answers,
            timeSpentSec: timeSpentSeconds,
            elapsedMs: elapsedTime,
            vars: (quiz.vars ?? {}) as Record<string, unknown>,
          })

          const result: QuizSolveResult = {
            correct: apiResult.isCorrect,
            corrects: apiResult.corrects ?? [],
            correctAnswers: (apiResult.correctAnswers ?? []).map(String),
            description: apiResult.description
              ? { text: apiResult.description.text ?? undefined }
              : undefined,
          }
          if (!assessmentMode) setSolveResult(result)

          if (!assessmentMode && resultDisplayMode === 'popup') {
            const projectedStreak = [...streak]
            projectedStreak[streakIndex] = apiResult.isCorrect
            const isCombo = isConsecutiveThree(projectedStreak, streakIndex)
            setPopup({ isCorrect: apiResult.isCorrect, isCombo })
          }

          onComplete?.({
            quizId,
            isCorrect: apiResult.isCorrect,
            score: apiResult.isCorrect ? 1 : 0,
          })
        } else {
          // QuizScript API path
          const solveResponse = await quizScriptApi.solve(quizId, {
            answers,
            vars: quiz.vars || {},
            elapsed: timeSpentSeconds,
          })

          const result = toQuizSolveResult(solveResponse)
          if (!assessmentMode) setSolveResult(result)

          if (!assessmentMode && resultDisplayMode === 'popup') {
            const projectedStreak = [...streak]
            projectedStreak[streakIndex] = solveResponse.correct
            const isCombo = isConsecutiveThree(projectedStreak, streakIndex)
            setPopup({ isCorrect: solveResponse.correct, isCombo })
          }

          onComplete?.({
            quizId,
            isCorrect: solveResponse.correct,
            score: solveResponse.correct ? 1 : 0,
          })
        }
      } catch (error) {
        console.error('Failed to submit quiz:', error)
        // 진단(assessment) 모드에서는 제출 실패를 호출자에게 전파해 advance를 막는다(답 유실 방지).
        if (assessmentMode) throw error
      }
    },
    [quiz, sessionId, onQuizResponse, quizScriptApi, onComplete, resultDisplayMode, streak, streakIndex, assessmentMode]
  )

  const handleAssessmentNext = useCallback(async () => {
    if (!isDone || submitting) return
    const quizId = quiz.quizId || quiz.scriptId || ''
    setSubmitting(true)
    try {
      await handleSolve(quizId, currentAnswers, quiz.vars || {}, Date.now() - mountTime)
      onAdvance?.()
    } catch {
      // 제출 실패 — advance 하지 않음(답 유실 방지). 사용자는 다시 '다음' 시도 가능.
    } finally {
      setSubmitting(false)
    }
  }, [isDone, submitting, quiz, currentAnswers, mountTime, handleSolve, onAdvance])

  const quizType = quiz.type || 'multi_choice'

  const hasHint = showHint && !!quiz.hint

  return (
    <div
      className={cn(
        'relative bg-white p-6',
        isCardEndAssessment
          ? 'min-h-px w-full max-w-[720px] flex-1 overflow-x-hidden overflow-y-auto rounded-2xl shadow-[0_8px_24px_0_rgb(0_0_0/0.05)]'
          : isAiq
            ? 'min-h-[calc(100dvh-110px)] w-full max-w-[720px] flex-1 overflow-x-hidden overflow-y-auto rounded-t-2xl shadow-[0_8px_24px_0_rgb(0_0_0/0.05)]'
            : 'rounded-xl shadow-lg',
      )}
    >
      {/* 깨구단 버튼 (프리셋 매핑 있을 때만 표시) */}
      {!assessmentMode && queBoardPresets && (
        <button
          onClick={() => setQueBoardOpen(true)}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
          title="깨구단 열기"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
          깨구단
        </button>
      )}
      {!assessmentMode && hasHint && (
        <QuizHintToggle hint={quiz.hint!} disabled={!!solveResult} />
      )}
      <QuizRenderer
        quizType={quizType}
        quizId={quizKey}
        quiz={quiz as any}
        onSolve={handleSolve}
        solveResult={solveResult}
        difficultyLevel={quiz.difficultyLevel}
        mode={mode}
        hideSubmitButton={assessmentMode}
        onAnswerChange={setCurrentAnswers}
      />
      {/* FloatingQueBoard */}
      {queBoardOpen && queBoardPresets && (
        <FloatingQueBoard
          problems={queBoardPresets}
          onClose={() => setQueBoardOpen(false)}
        />
      )}
      {!assessmentMode && (
        <QuizResultPopup
          isOpen={popup !== null}
          isCorrect={popup?.isCorrect ?? false}
          isCombo={popup?.isCombo ?? false}
          onClose={() => setPopup(null)}
          disableSound={disableSound}
        />
      )}
      {assessmentMode && (
        <div
          data-testid={`assessment-action-${assessmentActionPlacement}`}
          className={assessmentActionPlacement === 'card-end'
            ? 'mt-6 flex justify-end'
            : 'fixed bottom-0 left-1/2 z-50 w-full max-w-[720px] -translate-x-1/2'}
        >
          <button
            onClick={handleAssessmentNext}
            disabled={!isDone || submitting}
            className={cn(
              isDone && !submitting
                ? (assessmentActionPlacement === 'card-end'
                    ? QUIZ_SESSION_CARD_END_STYLES[variant].enabled
                    : QUIZ_SESSION_NEXT_STYLES[variant].enabled)
                : (assessmentActionPlacement === 'card-end'
                    ? QUIZ_SESSION_CARD_END_STYLES[variant].disabled
                    : QUIZ_SESSION_NEXT_STYLES[variant].disabled),
            )}
          >
            {nextLabel ?? '다음'}
          </button>
        </div>
      )}
    </div>
  )
}

export default QuizContainer
